import { Avatar, Box, IconButton, Typography } from "@mui/material";
import { FavoriteBorder, ChatBubbleOutline, SendOutlined, MoreHoriz, BookmarkBorder, ChevronLeft, ChevronRight } from "@mui/icons-material";
import { useState } from "react";
import { getMediaKind } from "../util";

interface Props {
  media?: string[];
  caption?: string;
  username?: string;
  avatarUrl?: string;
  timeLabel?: string;
}

export default function InstagramPostPreview({
  media = [],
  caption,
  username = "Your account",
  avatarUrl,
  timeLabel = "1w"
}: Props) {
  const [idx, setIdx] = useState(0);
  const count = media.length;

  const clamp = (n: number) => Math.min(Math.max(n, 0), Math.max(0, count - 1));
  const prev = () => setIdx((i) => clamp(i - 1));
  const next = () => setIdx((i) => clamp(i + 1));

  return (
    <Box sx={styles.root}>
      <Box sx={styles.left}>
        <Box sx={styles.viewport}>
          <Box sx={{ ...styles.track, transform: `translateX(-${idx * 100}%)` }}>
            {(count ? media : [undefined]).map((src, i) => {
              const isVideo = src ? getMediaKind(src) === "video" : false;
              return (
                <Box key={i} sx={styles.slide}>
                  {src ? (
                    <Box
                      component={isVideo ? "video" : "img"}
                      src={src}
                      autoPlay={isVideo ? true : undefined}
                      muted={isVideo ? true : undefined}
                      loop={isVideo ? true : undefined}
                      playsInline={isVideo ? true : undefined}
                      sx={styles.media}
                    />
                  ) : (
                    <Box sx={styles.noMedia}>
                      <Typography color="text.secondary">No media</Typography>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>

        {count > 1 && (
          <>
            {idx > 0 && (
              <IconButton aria-label="Previous" onClick={prev} sx={{ ...styles.arrow, left: 8 }}>
                <ChevronLeft />
              </IconButton>
            )}
            {idx < count - 1 && (
              <IconButton aria-label="Next" onClick={next} sx={{ ...styles.arrow, right: 8 }}>
                <ChevronRight />
              </IconButton>
            )}
            <Box sx={styles.dots}>
              {media.map((_, i) => (
                <Box key={i} sx={{ ...styles.dot, ...(i === idx ? styles.dotActive : {}) }} />
              ))}
            </Box>
          </>
        )}
      </Box>

      <Box sx={styles.right}>
        <Box sx={styles.header}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Avatar src={avatarUrl} sx={styles.avatar}>
              {username?.[0]?.toUpperCase() ?? "U"}
            </Avatar>
            <Typography sx={styles.username}>{username}</Typography>
            <Typography sx={styles.follow}>• Follow</Typography>
          </Box>
          <IconButton size="small" sx={styles.iconBtn}>
            <MoreHoriz />
          </IconButton>
        </Box>

        {caption?.trim() && (
          <Box sx={styles.captionRow}>
            <Typography sx={styles.username} component="span">
              {username}
            </Typography>
            <Typography component="span" sx={styles.captionText}>
              {" "}
              {caption}
            </Typography>
          </Box>
        )}

        <Box sx={styles.content}>
          <Box sx={styles.empty}>
            <Typography sx={styles.emptyTitle}>No comments yet.</Typography>
            <Typography sx={styles.emptySub}>Start the conversation.</Typography>
          </Box>
        </Box>

        <Box sx={styles.footer}>
          <Box sx={styles.actionsLeft}>
            <IconButton size="small">
              <FavoriteBorder />
            </IconButton>
            <IconButton size="small">
              <ChatBubbleOutline />
            </IconButton>
            <IconButton size="small">
              <SendOutlined />
            </IconButton>
          </Box>
          <IconButton size="small">
            <BookmarkBorder />
          </IconButton>
        </Box>

        <Typography sx={styles.timeLabel}>{timeLabel}</Typography>
      </Box>
    </Box>
  );
}

const styles = {
  root: {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", md: "minmax(280px, 480px) 420px" },
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 2,
    overflow: "hidden",
    bgcolor: "background.paper"
  },
  left: {
    position: "relative",
    bgcolor: "black",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  viewport: {
    width: "100%",
    aspectRatio: "4 / 5",
    maxHeight: { xs: 520, md: 680 },
    overflow: "hidden",
    position: "relative",
    bgcolor: "black"
  },
  track: {
    display: "flex",
    width: "100%",
    height: "100%",
    transition: "transform 260ms ease"
  },
  slide: {
    flex: "0 0 100%",
    width: "100%",
    height: "100%",
    position: "relative",
    bgcolor: "black"
  },
  media: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
    backgroundColor: "black"
  },
  noMedia: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    bgcolor: "grey.100"
  },
  arrow: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    color: "common.white",
    bgcolor: "rgba(0,0,0,0.35)",
    "&:hover": { bgcolor: "rgba(0,0,0,0.55)" },
    width: 36,
    height: 36,
    borderRadius: "50%",
    zIndex: 2
  },
  dots: {
    position: "absolute",
    bottom: 8,
    left: "50%",
    transform: "translateX(-50%)",
    display: "flex",
    gap: 0.75
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    bgcolor: "rgba(255,255,255,0.5)"
  },
  dotActive: {
    bgcolor: "common.white"
  },
  right: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100%"
  },
  header: {
    px: 2,
    py: 1.25,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
  },
  avatar: {
    width: 32,
    height: 32
  },
  username: {
    fontWeight: 600,
    fontSize: "0.95rem"
  },
  follow: {
    color: "primary.main",
    fontWeight: 600,
    fontSize: "0.9rem"
  },
  iconBtn: {
    color: "text.primary",
    p: 0.5,
    m: 0
  },
  captionRow: {
    px: 2,
    py: 1,
    lineHeight: 1.4
  },
  captionText: {
    whiteSpace: "pre-wrap",
    wordBreak: "break-word"
  },
  content: {
    flex: "1 1 0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    px: 2
  },
  empty: {
    textAlign: "center"
  },
  emptyTitle: {
    fontWeight: 700,
    fontSize: "1.25rem"
  },
  emptySub: {
    color: "text.secondary"
  },
  footer: {
    px: 1,
    py: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
  },
  actionsLeft: {
    display: "flex",
    alignItems: "center",
    gap: 0.5
  },
  timeLabel: {
    px: 2,
    pb: 2,
    color: "text.secondary",
    fontSize: "0.8rem"
  }
};
